import type { FC } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import moment from 'moment';
import { SvgIconType } from '@thermenresorts/icons';
import type ApiBathCard from 'src/core/models/api/ApiBathCard';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Styles from 'src/core/utils/Styles';
import Translations from 'src/core/utils/Translations';
import { boldSegments } from 'src/core/utils/RichText';
import { BathCardVariant } from 'src/domain/models/BathCardVariant';
import CardPattern from 'src/presentation/components/atoms/CardPattern';
import SkeletonBlock from 'src/presentation/components/atoms/SkeletonBlock';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import Barcode from 'src/presentation/components/molecules/Barcode';
import CardLinkAction from 'src/presentation/components/molecules/CardLinkAction';
import { TertiaryButton, TertiaryOutlineButton } from 'src/presentation/components/molecules/Button';

const LOW_BALANCE_THRESHOLD = 2;

interface Props {
	card: ApiBathCard;
	variant: BathCardVariant;
	width?: number;
	onPressView?: () => void;
	onPressReserve: () => void;
	onPressBuy?: () => void;
	onPressRemove: () => void;
}

const expiryDate = (card: ApiBathCard): string | null =>
	card.expire_at === null
		? null
		: moment(card.expire_at, Constants.dateFormatServerDateTime).format(Constants.dateFormatDisplayNumeric);

export const isBathCardDepleted = (card: ApiBathCard): boolean => !card.is_valid || card.balance <= 0;

export const bathCardSummary = (card: ApiBathCard): string => {
	// An expired card cannot be booked against, so whatever is left on it is not a visit any more.
	const balance = card.is_valid ? card.balance : 0;
	const visits = balance === 1 ? Translations.bathcardVisitOne : Translations.bathcardVisitsOther(balance);
	const workspace = card.workspace.title;
	const date = expiryDate(card);

	if (date === null) {
		return Translations.bathcardCardSummaryWithoutExpiry({ visits, workspace });
	}

	return Translations.bathcardCardSummary({ visits, date, workspace });
};

const BathCardCard: FC<Props> = ({ card, variant, width, onPressView, onPressReserve, onPressBuy, onPressRemove }) => {
	const isDepleted = isBathCardDepleted(card);
	const isRunningLow = !isDepleted && card.balance <= LOW_BALANCE_THRESHOLD;
	const canBuy = variant === BathCardVariant.Summary && onPressBuy !== undefined;
	const canView = variant === BathCardVariant.Summary && onPressView !== undefined;
	const title = variant === BathCardVariant.Barcode ? card.name : Translations.bathcardYourBathcard;

	return (
		<View style={[styles.card, Styles.card.shadowSoft, width === undefined ? null : { width }]}>
			<View style={styles.patternClip}>
				<CardPattern />
			</View>

			<View style={styles.header}>
				<SvgIcon name={SvgIconType.cardAlt} size={Dimens.iconSize16} style={styles.headerIcon} tintColor={Colors.white} />
				<Text numberOfLines={1} style={styles.name}>
					{title}
				</Text>
			</View>

			<Text style={styles.summary}>
				{boldSegments(bathCardSummary(card)).map((segment, index) => (
					<Text key={index} style={segment.bold ? styles.summaryStrong : undefined}>
						{segment.text}
					</Text>
				))}
			</Text>

			{variant === BathCardVariant.Barcode ? (
				<View accessibilityLabel={card.barcode} accessible={true} style={styles.barcode}>
					<Barcode
						barHeight={Dimens.size44}
						barcode={card.barcode}
						format={Constants.barcodeFormatEan13}
						style={styles.barcodeInner}
					/>
				</View>
			) : null}

			<View style={styles.footer}>
				{isDepleted ? (
					canBuy ? (
						<TertiaryButton label={Translations.bathcardBuyNewCard} onPress={onPressBuy} />
					) : null
				) : variant === BathCardVariant.Barcode ? (
					<TertiaryButton
						disabled={card.reservation_flow_url === null}
						label={Translations.bathcardReserve}
						onPress={onPressReserve}
					/>
				) : (
					<View style={styles.actions}>
						{canView ? (
							<TertiaryOutlineButton
								extraStyle={styles.action}
								label={Translations.bathcardView}
								textColor={Colors.white}
								onPress={onPressView}
							/>
						) : null}
						<TertiaryButton
							disabled={card.reservation_flow_url === null}
							extraStyle={styles.action}
							label={Translations.bathcardReserve}
							onPress={onPressReserve}
						/>
					</View>
				)}

				{isRunningLow && canBuy ? <CardLinkAction label={Translations.bathcardBuyNewCard} onPress={onPressBuy} /> : null}
				<CardLinkAction
					label={isDepleted ? Translations.bathcardRemoveThisCard : Translations.bathcardUnlinkThisCard}
					onPress={onPressRemove}
				/>
			</View>
		</View>
	);
};

export const BathCardSkeleton: FC<{ width?: number }> = ({ width }) => (
	<View
		accessibilityLabel={Translations.bathcardLoading}
		accessibilityRole="progressbar"
		accessibilityState={{ busy: true }}
		accessible={true}
		style={[styles.card, Styles.card.shadowSoft, width === undefined ? null : { width }]}
	>
		<View style={styles.patternClip}>
			<CardPattern />
		</View>

		<View style={styles.header}>
			<SvgIcon name={SvgIconType.cardAlt} size={Dimens.iconSize16} style={styles.headerIcon} tintColor={Colors.white} />
			<SkeletonBlock height={Dimens.size18} style={styles.skeletonOnCard} width={Dimens.size120} />
		</View>

		<View style={styles.skeletonSummary}>
			<SkeletonBlock height={Dimens.size14} style={styles.skeletonOnCard} width={Dimens.size200} />
			<SkeletonBlock height={Dimens.size14} style={styles.skeletonOnCard} width={Dimens.size120} />
		</View>

		<View style={styles.actions}>
			<SkeletonBlock height={Dimens.buttonHeight50} round={true} style={styles.skeletonAction} />
			<SkeletonBlock height={Dimens.buttonHeight50} round={true} style={styles.skeletonAction} />
		</View>
	</View>
);

const styles = StyleSheet.create({
	card: {
		backgroundColor: Colors.green900,
		borderRadius: Dimens.borderRadius4,
		padding: Dimens.padding20,
		gap: Dimens.gap16,
	},
	patternClip: {
		...StyleSheet.absoluteFill,
		borderRadius: Dimens.borderRadius4,
		overflow: 'hidden',
	},
	header: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: Dimens.gap10,
	},
	headerIcon: {
		marginTop: Dimens.padding5,
	},
	name: {
		flex: Dimens.flex1,
		color: Colors.white,
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize18,
		lineHeight: Dimens.lineHeight24,
	},
	summary: {
		color: Colors.white,
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight22,
	},
	summaryStrong: {
		fontFamily: Fonts.barlowBold,
	},
	barcode: {
		borderRadius: Dimens.borderRadius8,
		overflow: 'hidden',
	},
	barcodeInner: {
		paddingVertical: Dimens.padding12,
		paddingHorizontal: Dimens.padding16,
	},
	footer: {
		marginTop: 'auto',
		gap: Dimens.gap16,
	},
	actions: {
		flexDirection: 'row',
		gap: Dimens.gap12,
	},
	action: {
		flex: Dimens.flex1,
	},
	skeletonOnCard: {
		backgroundColor: Colors.white33,
	},
	skeletonAction: {
		flex: Dimens.flex1,
		backgroundColor: Colors.white33,
	},
	skeletonSummary: {
		gap: Dimens.gap8,
	},
});

export default BathCardCard;
